// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import dayjs from 'dayjs';
import { StorageKeys } from './Enums';
import { COLLECT_SESSION_TTL } from './ConstantValues';
import { toast } from 'react-toastify';
import i18n from 'i18n';

type CollectingSession = {
  siteMap: Record<string, boolean>;
  createdAt: string;
  processedSiteIds?: string[];
};

/**
 * Manages collecting route sessions stored in localStorage.
 * Each session is keyed by routeId and tracks selected sites and processed sites
 * for a collection run.
 */
export class CollectingRouteSession {
  /**
   * Checks if a given session has expired based on its creation time and the defined TTL.
   * @param session - The collecting session to check.
   * @returns `true` if the session has expired, `false` otherwise.
   */
  static checkSessionExpired(session: CollectingSession): boolean {
    return (
      Math.abs(dayjs(session.createdAt).diff(dayjs(), 'milliseconds')) >=
      COLLECT_SESSION_TTL
    );
  }

  /**
   * Returns all collecting sessions from localStorage.
   * @returns A record mapping routeId to its CollectingSession data.
   */
  static getAll(): Record<string, CollectingSession> {
    const data = localStorage.getItem(StorageKeys.COLLECTING_SESSION);

    if (data) {
      try {
        return JSON.parse(data);
      } catch {
        localStorage.removeItem(StorageKeys.COLLECTING_SESSION);

        return {};
      }
    }

    return {};
  }

  /**
   * Returns the session for a given routeId.
   * Returns `undefined` if routeId is `'none'` or the session does not exist.
   * @param routeId - The route ID to look up.
   */
  static get(
    routeId: number | string,
    checkExpired = true
  ): CollectingSession | undefined {
    if (routeId === 'none') {
      return undefined;
    }

    const session = this.getAll()[routeId];

    if (session && this.checkSessionExpired(session) && checkExpired) {
      toast.info(i18n.t('messages.M_route_collect_expired'));
      this.remove(routeId);

      return undefined;
    }

    return session;
  }

  /**
   * Saves or updates a collecting session for the given routeId.
   * Does nothing if routeId is `'none'` or if neither `siteMap` nor
   * `processedSiteIds` is provided and no existing siteMap is found.
   * @param params.routeId - The route ID to save the session under.
   * @param params.siteMap - A map of siteId → selected state.
   * @param params.processedSiteIds - List of already-processed site IDs.
   */
  static save(params: {
    routeId: number | string;
    siteMap?: Record<string, boolean>;
    processedSiteIds?: string[];
  }): void {
    const { routeId, siteMap, processedSiteIds } = params;

    if (routeId === 'none') {
      return;
    }

    const sessions = this.getAll();

    if (
      (!siteMap && !processedSiteIds) ||
      (!siteMap && !sessions[routeId]?.siteMap)
    ) {
      return;
    }

    const data = {
      ...sessions,
      [routeId]: {
        siteMap: siteMap || sessions[routeId]?.siteMap || {},
        createdAt: sessions[routeId]?.createdAt || new Date().toUTCString(),
        processedSiteIds:
          processedSiteIds || sessions[routeId]?.processedSiteIds,
      },
    };

    localStorage.setItem(StorageKeys.COLLECTING_SESSION, JSON.stringify(data));
  }

  /**
   * Removes all collecting sessions from localStorage.
   */
  static removeAll(): void {
    localStorage.removeItem(StorageKeys.COLLECTING_SESSION);
  }

  /**
   * Removes the session for a given routeId.
   * Does nothing if routeId is `'none'`.
   * @param routeId - The route ID whose session should be removed.
   */
  static remove(routeId: number | string): void {
    if (routeId === 'none') {
      return;
    }

    const sessions = this.getAll();

    delete sessions[routeId];

    localStorage.setItem(
      StorageKeys.COLLECTING_SESSION,
      JSON.stringify(sessions)
    );
  }

  /**
   * Adds a siteId to the processed sites list of an existing session.
   * Silently ignores if routeId is `'none'` or the session does not exist.
   * Prevents duplicate entries by removing any prior occurrence of the siteId.
   * @param params.routeId - The route ID of the session to update.
   * @param params.siteId - The site ID to mark as processed.
   */
  static addProcessedSite(params: {
    routeId: number | string;
    siteId: string;
  }): void {
    const { routeId, siteId } = params;

    if (routeId === 'none') {
      return;
    }

    const session = this.get(routeId, false);

    if (!session) {
      return;
    }

    const savedSiteIds = session.processedSiteIds || [];

    this.save({
      routeId,
      processedSiteIds: [...savedSiteIds.filter((id) => id !== siteId), siteId],
    });
  }

  /**
   * Removes specific site IDs from the processed sites list of a session.
   * Silently ignores if routeId is `'none'` or the session does not exist.
   * @param params.routeId - The route ID of the session to update.
   * @param params.siteIds - The site IDs to remove from the processed list.
   */
  static removeProcessedSites(params: {
    routeId: number | string;
    siteIds: string[];
  }): void {
    const { routeId, siteIds } = params;

    if (routeId === 'none') {
      return;
    }

    const session = this.get(routeId, false);

    if (!session) {
      return;
    }

    const savedSiteIds = session.processedSiteIds || [];

    this.save({
      routeId,
      processedSiteIds: savedSiteIds.filter((id) => !siteIds.includes(id)),
    });
  }
}

export const isRouteIdExist = (
  routeId: number | string | null | undefined
): boolean => {
  return routeId != null && routeId !== 'all' && routeId !== 'none';
};
